import { useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { useHistory } from 'react-router-dom';

import { HEADER_ID } from '@/components/templates/PageLayout';
import { ReactSingleSelectInput, SelectForm } from '@/components/uiParts';
import { useTargetsDomHeight } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { errorHandler } from '@/lib/ErrorHandler';
import { HttpError } from '@/lib/exception';
import { LocalStorageHandler } from '@/lib/LocalStorageHandler';
import { nullToEmptyString } from '@/lib/util';
import { BUTTON_AREA_ID } from '@/pages/menu-managements/components/TopButtonArea';
import {
  MenuFormError,
  validateMemo,
  validateTitleJa,
} from '@/pages/menu-managements/menus/validate';
import {
  getSearchItems,
  SearchItem,
} from '@/pages/menu-managements/services/searchItem';
import { routes } from '@/router';
import { useAssociateBrandList } from '@/services/AssociateBrand/hooks/list/useAssociateBrandList';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import { useMenuManagementSearchBrandCategories } from '@/services/BrandCategory/hooks/search/useMenuManagementSearchBrandCategories';
import {
  useMenuManagementBrandMenuShow,
  useMenuManagementCreateBrandMenu,
  useMenuManagementUpdateBrandMenu,
} from '@/services/BrandMenu/hooks';

type Return = {
  associateBrandSelectInput: ReactSingleSelectInput;
  titleJa: string;
  memo: string;
  error: MenuFormError;
  searchItems: SearchItem[];
  selectedItems: SearchItem[];
  searchItemName: string;
  isSearching: boolean;
  targetsHeight: number;
  initialStringifyFormState: string;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  handleCreate: () => void;
  handleUpdate: (urlParamsId: string) => void;
  handleInputTitleJa: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputMemo: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleOnDragEnd: (result: DropResult) => void;
  handleSearchItemNameInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleAddSelectItems: (item: SearchItem) => void;
  handleDeleteDragItem: (itemId: string) => void;
  initializeNewPageState: () => Promise<void>;
  initializeEditPageState: (urlParamsId: string) => Promise<void>;
};

export const useMenuManagementMenuForm = (): Return => {
  const history = useHistory();
  const { managerAccountMe } = useManagerAccountMe();
  /* アクセス制御 */
  redirect404Page('menu_management', history, managerAccountMe);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(0);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [titleJa, setTitleJa] = useState<string>('');
  const errorObj = { message: '', isError: false, isTouched: false };
  const [error, setError] = useState({
    titleJa: { ...errorObj },
    memo: { ...errorObj },
  });
  const [associateBrandSelectOptions, setAssociateBrandSelectOptions] =
    useState<SelectForm[]>([]);
  const [brandSelect, setBrandSelect] = useState<SelectForm | null>({
    label: '',
    value: '',
  });
  const [memo, setMemo] = useState<string>('');
  const [searchItemName, setSearchItemName] = useState<string>('');
  const [searchItems, setSearchItems] = useState<SearchItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<SearchItem[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setIsDragged] = useState<boolean>(false);
  const [initialStringifyFormState, setInitialStringifyFormState] =
    useState<string>('');

  const handleInputTitleJa = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitleJa = e.target.value;
    const newError = validateTitleJa(newTitleJa, error);
    setTitleJa(newTitleJa), setError(newError);
  };

  const handleInputMemo = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newMemo = e.target.value;
    const newError = validateMemo(newMemo, error);
    setMemo(newMemo), setError(newError);
  };
  const handleSearchItemNameInput = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputTitle = e.target.value;
    setSearchItemName(inputTitle);
    setIsSearching(true);
    clearTimeout(Number(timer));
    const newTimer = setTimeout(async () => {
      if (brandSelect === null) return;
      const categories = await useMenuManagementSearchBrandCategories({
        associate_brand_id: brandSelect.value,
        title: inputTitle,
      });

      setSearchItems(categories);
      setIsSearching(false);
    }, 700);
    setTimer(Number(newTimer));
  };

  const handleAddSelectItems = (item: SearchItem) => {
    setSearchItemName('');
    const items = [item, ...selectedItems];
    setSelectedItems(items);
  };

  const associateBrandSelectInput = {
    value: brandSelect,
    options: associateBrandSelectOptions,
    onChange: (newBrand: SelectForm | null) => {
      if (newBrand?.value === brandSelect?.value) return;
      setBrandSelect(newBrand);
      setSelectedItems([]);
    },
    onReset: () => {
      setBrandSelect({
        value: '',
        label: '',
      });
    },
  };

  const handleOnDragEnd = (result: DropResult) => {
    const [reorderedItem] = selectedItems.splice(result.source.index, 1);
    selectedItems.splice(result.destination?.index ?? 0, 0, reorderedItem);
    setIsDragged((bool) => !bool);
    setSelectedItems(selectedItems);
  };

  const handleDeleteDragItem = (itemId: string) => {
    const newSelectedItems = selectedItems.filter((item) => item.id !== itemId);
    setSelectedItems(newSelectedItems);
  };

  const listUrl = routes.menuManagementMenus.path;

  const handleCreate = () => {
    (async () => {
      setIsLoading(true);
      await useMenuManagementCreateBrandMenu({
        associate_brand_id: Number(associateBrandSelectInput.value?.value),
        title: {
          translation: {
            ja: titleJa,
          },
        },
        memo: memo !== '' ? memo : null,
        brand_category_sort_objects: getSearchItems(selectedItems),
      });
      setIsLoading(false);
      alert('メニューを登録しました。');
      history.push(listUrl);
    })();
  };

  const handleUpdate = (urlParamsId: string) => {
    (async () => {
      try {
        setIsLoading(true);
        await useMenuManagementUpdateBrandMenu(urlParamsId, {
          associate_brand_id: Number(associateBrandSelectInput.value?.value),
          title: {
            translation: {
              ja: titleJa,
            },
          },
          memo: memo !== '' ? memo : null,
          brand_category_sort_objects: getSearchItems(selectedItems),
        });
        setIsLoading(false);
        alert('メニューを更新しました。');
        history.push(listUrl);
      } catch (error) {
        setIsLoading(false);
        if (error instanceof HttpError && error.status === 400) {
          return alert(error.message);
        }
        // @ts-ignore TS2322
        errorHandler({ error });
      }
    })();
  };

  const { targetsHeight } = useTargetsDomHeight([HEADER_ID, BUTTON_AREA_ID]);

  const initializeNewPageState = async () => {
    const associateBrands = await useAssociateBrandList();
    const associateBrandOptions = associateBrands.map((brand) => {
      return {
        label: brand.name,
        value: brand.id.toString(),
      };
    });
    const { associateBrand } = LocalStorageHandler.get('menuManagement');
    const initialAssociateBrand =
      associateBrand !== undefined ? associateBrand : associateBrandOptions[0];
    setBrandSelect(initialAssociateBrand);
    LocalStorageHandler.set('menuManagement', {
      associateBrand: initialAssociateBrand,
    });
    setAssociateBrandSelectOptions(associateBrandOptions);
  };

  const initializeEditPageState = async (urlParamsId: string) => {
    const { associateBrand } = LocalStorageHandler.get('menuManagement');
    if (associateBrand === undefined) return history.push(listUrl);
    const brandMenu = await useMenuManagementBrandMenuShow(urlParamsId);
    setBrandSelect(associateBrand);
    const newTitleJa = brandMenu.title.translation.ja;
    const newMemo = nullToEmptyString(brandMenu.memo);
    const newSelectedItems = brandMenu.brand_category_sort_objects;
    setTitleJa(newTitleJa);
    setMemo(newMemo);
    setSelectedItems(newSelectedItems);
    setInitialStringifyFormState(
      JSON.stringify([newTitleJa, newMemo, newSelectedItems])
    ); // WARNING 文字列比較なので順番を変更するときは注意
  };

  return {
    associateBrandSelectInput,
    titleJa,
    memo,
    error,
    searchItems,
    selectedItems,
    searchItemName,
    isSearching,
    targetsHeight,
    initialStringifyFormState,
    isLoading,
    setIsLoading,
    handleCreate,
    handleUpdate,
    handleInputTitleJa,
    handleInputMemo,
    handleOnDragEnd,
    handleSearchItemNameInput,
    handleAddSelectItems,
    handleDeleteDragItem,
    initializeNewPageState,
    initializeEditPageState,
  };
};
